import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import CurrencySuperscriptCents from '../currency-superscript-cents.gts';
import { formatISODateToDisplay } from '../../utils/date';
import Popover from 'tio-reimagined/components/popover';
import type { IntlService } from 'ember-intl';
import type { LimitsStatusDataSignature } from '../../services/tuition-assistance';

const CREDITS_CAPACITY = 'TAS.LimitCapacity.CREDITS';

interface BenefitHistorySummarySignature {
  Args: {
    limitsStatusPrevious?: LimitsStatusDataSignature;
    limitsStatusCurrent?: LimitsStatusDataSignature;
    limitsStatusNext?: LimitsStatusDataSignature;
    externalSpendLabel: string;
  };
}

export default class BenefitHistorySummary extends Component<BenefitHistorySummarySignature> {
  @service declare intl: IntlService;

  toNumber = (value: unknown) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return 0;
  };

  hasCreditCapacity = (status?: LimitsStatusDataSignature) => {
    return status?.limit_capacity === CREDITS_CAPACITY;
  };

  formatCredits = (amount: number) => {
    return this.intl.t('tas.dashboard.annual_benefits.credits_count', { count: amount });
  };

  get minVisiblePercentage() {
    return 5;
  }

  get shouldShowNextYear() {
    const nextStatus = this.args.limitsStatusNext;
    if (!nextStatus?.annual_start_date) {
      return false;
    }

    const nextStart = new Date(nextStatus.annual_start_date);
    if (Number.isNaN(nextStart.getTime())) {
      return false;
    }

    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() + 6);
    return nextStart <= cutoff;
  }

  get summaryCards() {
    const cards = [
      {
        label: this.intl.t('tas.benefit_history.last_year'),
        status: this.args.limitsStatusPrevious,
      },
      {
        label: this.intl.t('tas.benefit_history.current_year'),
        status: this.args.limitsStatusCurrent,
      },
    ];

    if (this.shouldShowNextYear) {
      cards.push({
        label: this.intl.t('tas.benefit_history.next_year'),
        status: this.args.limitsStatusNext,
      });
    }

    return cards;
  }

  get summaryGridClass() {
    const base = 'grid gap-4';
    const count = this.summaryCards.length;

    if (count <= 1) {
      return `${base} md:grid-cols-1`;
    }

    if (count === 2) {
      return `${base} md:grid-cols-2`;
    }

    return `${base} md:grid-cols-3`;
  }

  getAnnualLimit = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const batchLimit = this.toNumber(status.details?.batch?.annual_limit);
    const schemeLimit = this.toNumber(status.details?.scheme?.annual_limit);
    const productLimit = this.toNumber(status.details?.product?.annual_limit);
    const companyLimit = this.toNumber(status.details?.company?.annual_limit);
    const maxConfiguredLimit = Math.max(batchLimit, schemeLimit, productLimit, companyLimit);
    const batchFallbackLimit = this.toNumber(status.details?.batch?.limit);
    const schemeFallbackLimit = this.toNumber(status.details?.scheme?.limit);
    const productFallbackLimit = this.toNumber(status.details?.product?.limit);
    const companyFallbackLimit = this.toNumber(status.details?.company?.limit);
    const maxFallbackLimit = Math.max(
      batchFallbackLimit,
      schemeFallbackLimit,
      productFallbackLimit,
      companyFallbackLimit
    );

    if (maxConfiguredLimit > 0) {
      return maxConfiguredLimit;
    }

    if (maxFallbackLimit > 0) {
      return maxFallbackLimit;
    }

    // Credit-based plans sometimes omit annual_limit while still returning
    // available/usage values; derive total limit so the bar graph can render.
    if (this.hasCreditCapacity(status)) {
      const available = this.getAvailableAmount(status);
      if (available !== null && available !== undefined) {
        return Math.max(0, available + this.getTotalUsed(status));
      }
    }

    return maxConfiguredLimit;
  };

  getAvailableAmount = (status?: LimitsStatusDataSignature) => {
    if (!status) return null;

    // Prefer admin override if present so UI reflects overridden limits correctly
    if (status.available_with_override !== null && status.available_with_override !== undefined) {
      return this.toNumber(status.available_with_override);
    }

    if (status.available !== null && status.available !== undefined) {
      return this.toNumber(status.available);
    }

    const batchAvailable = this.toNumber(status.details?.batch?.available);
    const schemeAvailable = this.toNumber(status.details?.scheme?.available);
    const productAvailable = this.toNumber(status.details?.product?.available);
    const companyAvailable = this.toNumber(status.details?.company?.available);

    const availableValues = [
      batchAvailable,
      schemeAvailable,
      productAvailable,
      companyAvailable,
    ].filter((value) => value > 0);

    if (availableValues.length === 0) {
      return null;
    }

    return Math.max(...availableValues);
  };

  getPaidAmount = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const batchUsage = status.details?.batch?.annual_usage ?? 0;
    const schemeUsage = status.details?.scheme?.annual_usage ?? 0;
    const productUsage = status.details?.product?.annual_usage ?? 0;
    const companyUsage = status.details?.company?.annual_usage ?? 0;

    return Math.max(batchUsage, schemeUsage, productUsage, companyUsage);
  };

  getExternalAmount = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const batchExternal = status.details?.batch?.external_annual_usage ?? 0;
    const schemeExternal = status.details?.scheme?.external_annual_usage ?? 0;
    const productExternal = status.details?.product?.external_annual_usage ?? 0;
    const companyExternal = status.details?.company?.external_annual_usage ?? 0;
    const rootExternal = status.external_annual_usage ?? 0;

    return Math.max(batchExternal, schemeExternal, productExternal, companyExternal, rootExternal);
  };

  getTotalUsed = (status?: LimitsStatusDataSignature) => {
    return this.getPaidAmount(status) + this.getExternalAmount(status);
  };

  hasExternalSpend = (status?: LimitsStatusDataSignature) => {
    return this.getExternalAmount(status) > 0;
  };

  hasAnnualLimit = (status?: LimitsStatusDataSignature) => {
    return this.getAnnualLimit(status) > 0;
  };

  getRemainingAmount = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;

    // Monetary cards in production calculate remaining as annual cap minus paid usage.
    if (!this.hasCreditCapacity(status)) {
      const annualLimit = this.getAnnualLimit(status);
      if (annualLimit > 0) {
        return Math.max(0, annualLimit - this.getPaidAmount(status));
      }
    }

    const available = this.getAvailableAmount(status);
    if (available !== null && available !== undefined) {
      // Keep monetary behavior aligned with current production display.
      // For credit-capacity programs we still account for external spend.
      if (this.hasCreditCapacity(status)) {
        return Math.max(0, available - this.getExternalAmount(status));
      }

      return Math.max(0, available);
    }

    const annualLimit = this.getAnnualLimit(status);
    if (!annualLimit) return 0;
    return Math.max(0, annualLimit - this.getTotalUsed(status));
  };

  getPaidPercentage = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const annualLimit = this.getAnnualLimit(status);
    const totalUsed = this.getTotalUsed(status);
    if (!annualLimit || totalUsed <= 0) return 0;
    const rawPercentage = (totalUsed / annualLimit) * 100;
    return Math.min(100, Math.max(this.minVisiblePercentage, rawPercentage));
  };

  getTotalUsedPercentage = (status?: LimitsStatusDataSignature) => {
    return this.getPaidPercentage(status);
  };

  getPaidSegmentPercentage = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const totalUsed = this.getTotalUsed(status);
    const paidAmount = this.getPaidAmount(status);
    if (!this.hasAnnualLimit(status) || totalUsed <= 0 || paidAmount <= 0) return 0;
    return (paidAmount / totalUsed) * this.getTotalUsedPercentage(status);
  };

  getExternalSegmentPercentage = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const totalUsed = this.getTotalUsed(status);
    const externalAmount = this.getExternalAmount(status);
    if (!this.hasAnnualLimit(status) || totalUsed <= 0 || externalAmount <= 0) return 0;
    return (externalAmount / totalUsed) * this.getTotalUsedPercentage(status);
  };

  getPaidStyle = (status?: LimitsStatusDataSignature) => {
    return htmlSafe(`width: ${this.getPaidSegmentPercentage(status)}%`);
  };

  getExternalStyle = (status?: LimitsStatusDataSignature) => {
    return htmlSafe(`width: ${this.getExternalSegmentPercentage(status)}%`);
  };

  formatAmount = (cents: number) => {
    return this.intl.formatNumber(cents / 100, { style: 'currency', currency: 'USD' });
  };

  getPaidLabel = (status?: LimitsStatusDataSignature) => {
    if (this.hasCreditCapacity(status)) {
      return this.intl.t('tas.dashboard.annual_benefits.used') as string;
    }

    return this.intl.t('tas.dashboard.annual_benefits.paid') as string;
  };

  getPaidAmount_formatted = (status?: LimitsStatusDataSignature) => {
    const amount = this.getPaidAmount(status);
    if (this.hasCreditCapacity(status)) {
      return this.formatCredits(amount);
    }

    return this.formatAmount(amount);
  };

  getExternalAmount_formatted = (status?: LimitsStatusDataSignature) => {
    const amount = this.getExternalAmount(status);
    if (this.hasCreditCapacity(status)) {
      return this.formatCredits(amount);
    }

    return this.formatAmount(amount);
  };

  getAnnualStartDate = (status?: LimitsStatusDataSignature) => {
    return status?.annual_start_date || '';
  };

  getAnnualEndDate = (status?: LimitsStatusDataSignature) => {
    return status?.annual_end_date || '';
  };

  <template>
    <div class="flex flex-col gap-4">
      <div class={{this.summaryGridClass}}>
        {{#each this.summaryCards as |card|}}
          <div class="border border-gray-200 rounded-xl p-4">
            <div class="text-sm text-gray-500 font-semibold">{{card.label}}</div>
            {{#if card.status}}
              {{#if (this.hasAnnualLimit card.status)}}
                <div class="text-2xl font-semibold text-gray-900 mt-2">
                  {{#if (this.hasCreditCapacity card.status)}}
                    {{this.formatCredits (this.getRemainingAmount card.status)}}
                  {{else}}
                    <CurrencySuperscriptCents
                      @amountCents={{this.getRemainingAmount card.status}}
                    />
                  {{/if}}
                </div>
                <div class="text-xs text-gray-500">
                  {{#if (this.hasCreditCapacity card.status)}}
                    {{t "tas.dashboard.annual_benefits.estimated_remaining_credits"}}
                  {{else}}
                    {{t "tas.dashboard.annual_benefits.estimated_remaining_benefit"}}
                  {{/if}}
                </div>
                <div class="mt-3">
                  <div class="w-full h-2 bg-gray-200 rounded-full overflow-hidden flex">
                    {{#if (this.getPaidSegmentPercentage card.status)}}
                      <Popover
                        @placement="top"
                        @triggerMode="hover"
                        @maxWidth={{200}}
                        @closeOnScroll={{false}}
                      >
                        <:trigger as |p|>
                          <div
                            class="h-full bg-violet-700 cursor-pointer"
                            style={{this.getPaidStyle card.status}}
                            role="button"
                            tabindex="0"
                            {{p.register}}
                            {{p.hoverHandlers}}
                            {{on "click" p.toggle}}
                          ></div>
                        </:trigger>
                        <:body>
                          <span class="font-semibold">
                            {{this.getPaidLabel card.status}}
                          </span>:
                          {{this.getPaidAmount_formatted card.status}}
                        </:body>
                      </Popover>
                    {{/if}}
                    {{#if (this.getExternalSegmentPercentage card.status)}}
                      <Popover
                        @placement="top"
                        @triggerMode="hover"
                        @maxWidth={{200}}
                        @closeOnScroll={{false}}
                      >
                        <:trigger as |p|>
                          <div
                            class="h-full bg-cyan-500 cursor-pointer"
                            style={{this.getExternalStyle card.status}}
                            role="button"
                            tabindex="0"
                            {{p.register}}
                            {{p.hoverHandlers}}
                            {{on "click" p.toggle}}
                          ></div>
                        </:trigger>
                        <:body>
                          <span class="font-semibold">{{@externalSpendLabel}}</span>:
                          {{this.getExternalAmount_formatted card.status}}
                        </:body>
                      </Popover>
                    {{/if}}
                  </div>
                  <div
                    class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-2"
                  >
                    <div class="flex items-center gap-1.5">
                      <span class="w-2 h-2 rounded-full bg-violet-700"></span>
                      <span class="text-gray-700">
                        {{#if (this.hasCreditCapacity card.status)}}
                          {{t "tas.dashboard.annual_benefits.used"}}:
                          {{this.getPaidAmount_formatted card.status}}
                        {{else}}
                          {{t "tas.dashboard.annual_benefits.paid"}}
                        {{/if}}
                      </span>
                    </div>
                    {{#if (this.hasExternalSpend card.status)}}
                      <div class="flex items-center gap-1.5">
                        <span class="w-2 h-2 rounded-full bg-cyan-500"></span>
                        <span class="text-gray-700">{{@externalSpendLabel}}</span>
                      </div>
                    {{/if}}
                  </div>
                  <div class="flex items-center justify-between text-xs text-gray-500 mt-2">
                    <span class="font-semibold">
                      {{formatISODateToDisplay
                        (this.getAnnualStartDate card.status)
                        this.intl
                        "2-digit"
                      }}
                      -
                      {{formatISODateToDisplay
                        (this.getAnnualEndDate card.status)
                        this.intl
                        "2-digit"
                      }}
                    </span>
                    <span>
                      {{t "tas.dashboard.annual_benefits.eligible_up_to"}}
                      <span class="font-semibold">
                        {{#if (this.hasCreditCapacity card.status)}}
                          {{this.formatCredits (this.getAnnualLimit card.status)}}
                        {{else}}
                          <CurrencySuperscriptCents
                            @amountCents={{this.getAnnualLimit card.status}}
                          />
                        {{/if}}
                      </span>
                    </span>
                  </div>
                </div>
              {{else}}
                <div class="text-2xl font-semibold text-gray-900 mt-2">
                  {{#if (this.hasCreditCapacity card.status)}}
                    {{this.formatCredits (this.getTotalUsed card.status)}}
                  {{else}}
                    <CurrencySuperscriptCents @amountCents={{this.getTotalUsed card.status}} />
                  {{/if}}
                </div>
                <div class="text-xs text-gray-500">
                  {{#if (this.hasCreditCapacity card.status)}}
                    {{t "tas.dashboard.annual_benefits.total_credits_used_this_year"}}
                  {{else}}
                    {{t "tas.dashboard.annual_benefits.total_used_this_year"}}
                  {{/if}}
                </div>
                <div class="mt-3">
                  <div class="flex items-center justify-between text-xs text-gray-500 mt-2">
                    <span class="font-semibold">
                      {{formatISODateToDisplay
                        (this.getAnnualStartDate card.status)
                        this.intl
                        "2-digit"
                      }}
                      -
                      {{formatISODateToDisplay
                        (this.getAnnualEndDate card.status)
                        this.intl
                        "2-digit"
                      }}
                    </span>
                  </div>
                </div>
              {{/if}}
            {{else}}
              <div class="text-2xl font-semibold text-gray-400 mt-2">—</div>
            {{/if}}
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}
